'use client';
import React from 'react';
import { BookOpen, Trophy, Clock, BarChart3, Flame, Star, ArrowRight, TrendingUp } from 'lucide-react';
import { QuizSession } from './QuizArenaContent';

interface QuizLobbyProps {
  quizBank: Record<string, { title: string; category: string; emoji: string; description: string; questions: { difficulty: string; points: number }[] }>;
  history: QuizSession[];
  onStart: (quizId: string) => void;
  onViewStats: () => void;
}

const DIFFICULTY_COLORS: Record<string, string> = {
  Easy: '#10B981',
  Medium: '#F59E0B',
  Hard: '#F43F5E',
};

export default function QuizLobby({ quizBank, history, onStart, onViewStats }: QuizLobbyProps) {
  const totalPlayed = history.length;
  const totalScore = history.reduce((s, h) => s + h.totalScore, 0);
  const avgAccuracy = history.length > 0
    ? Math.round(history.reduce((s, h) => {
        const correct = h.attempts.filter(a => a.isCorrect).length;
        return s + (correct / h.attempts.length) * 100;
      }, 0) / history.length)
    : 0;
  const bestScore = history.length > 0 ? Math.max(...history.map(h => h.totalScore)) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)' }}>
            🧠
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-foreground">Quiz Arena</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Test your knowledge, earn XP, climb the leaderboard</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {totalPlayed > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Quizzes Played', value: totalPlayed, icon: BookOpen, color: '#7C3AED' },
            { label: 'Total Score', value: totalScore.toLocaleString(), icon: Star, color: '#F59E0B' },
            { label: 'Avg Accuracy', value: `${avgAccuracy}%`, icon: TrendingUp, color: '#10B981' },
            { label: 'Best Score', value: bestScore.toLocaleString(), icon: Trophy, color: '#06B6D4' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <stat.icon size={14} style={{ color: stat.color }} />
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</span>
              </div>
              <p className="text-xl font-extrabold text-foreground">{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Quiz Cards */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground">Choose a Quiz</h2>
          {totalPlayed > 0 && (
            <button onClick={onViewStats}
              className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
              style={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA' }}>
              <BarChart3 size={14} />
              My Stats
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(quizBank).map(([id, quiz]) => {
            const maxPoints = quiz.questions.reduce((s, q) => s + q.points, 0);
            const difficulties = [...new Set(quiz.questions.map(q => q.difficulty))];
            const played = history.filter(h => h.quizId === id);
            const bestAttempt = played.length > 0 ? Math.max(...played.map(h => h.totalScore)) : null;

            return (
              <div key={id} className="rounded-2xl overflow-hidden group cursor-pointer transition-all duration-200 hover:scale-[1.02]"
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                onClick={() => onStart(id)}>
                {/* Card top accent */}
                <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #7C3AED, #06B6D4)' }} />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                      style={{ background: 'var(--muted)' }}>
                      {quiz.emoji}
                    </div>
                    {bestAttempt !== null && (
                      <div className="flex items-center gap-1 px-2 py-1 rounded-lg"
                        style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B' }}>
                        <Trophy size={11} />
                        <span className="text-xs font-bold">{bestAttempt}</span>
                      </div>
                    )}
                  </div>

                  <h3 className="font-bold text-foreground mb-1 leading-snug">{quiz.title}</h3>
                  <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{quiz.description}</p>

                  <div className="flex items-center gap-3 mb-4 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <span className="flex items-center gap-1"><BookOpen size={11} />{quiz.questions.length} questions</span>
                    <span className="flex items-center gap-1"><Star size={11} style={{ color: '#F59E0B' }} />{maxPoints} pts</span>
                    <span className="flex items-center gap-1"><Clock size={11} />~{Math.ceil(quiz.questions.length * 0.4)} min</span>
                  </div>

                  <div className="flex items-center gap-1.5 mb-4">
                    {difficulties.map(d => (
                      <span key={d} className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: `${DIFFICULTY_COLORS[d]}20`, color: DIFFICULTY_COLORS[d] }}>
                        {d}
                      </span>
                    ))}
                  </div>

                  <button className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm transition-all"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
                    {played.length > 0 ? <><Flame size={14} /> Play Again</> : <><ArrowRight size={14} /> Start Quiz</>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent History */}
      {history.length > 0 && (
        <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="font-bold text-foreground mb-4">Recent Attempts</h3>
          <div className="space-y-2">
            {history.slice(0, 5).map((session, i) => {
              const correct = session.attempts.filter(a => a.isCorrect).length;
              const accuracy = Math.round((correct / session.attempts.length) * 100);
              return (
                <div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-xl"
                  style={{ background: 'var(--muted)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm"
                      style={{ background: 'var(--card)' }}>
                      {quizBank[session.quizId]?.emoji ?? '🧠'}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{session.title}</p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        {correct}/{session.attempts.length} correct · {new Date(session.completedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold" style={{ color: '#F59E0B' }}>{session.totalScore} pts</p>
                    <p className="text-xs font-semibold" style={{ color: accuracy >= 70 ? '#10B981' : accuracy >= 40 ? '#F59E0B' : '#F43F5E' }}>
                      {accuracy}%
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
